import { useState } from "react";
import { Facebook, Twitter, Linkedin, MessageCircle, Mail, Link2, Share2, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../lib/api";

const track = (publicId, network) => {
  if (!publicId) return;
  axios.post(`${API}/stories/public/${publicId}/track`, { event: "share", network }).catch(() => {});
};

export const mediaUrl = (sid, mid, withAuth = false) =>
  `${API}/stories/media/${sid}/${mid}${withAuth ? `?auth=${localStorage.getItem("token") || ""}` : ""}`;

export function videoEmbed(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

const SHARES = (url, title) => [
  ["Facebook", Facebook, `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`],
  ["X", Twitter, `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`],
  ["LinkedIn", Linkedin, `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`],
  ["WhatsApp", MessageCircle, `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`],
  ["Pinterest", Share2, `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(title)}`],
  ["Reddit", Share2, `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`],
  ["Email", Mail, `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(url)}`],
];

/* One-tap WhatsApp + Facebook share for a published story (used on My Stories cards). */
export function QuickShare({ publicId, title, storyId }) {
  const cardUrl = `${API}/stories/share/${publicId}`;
  const links = [
    ["WhatsApp", MessageCircle, `https://wa.me/?text=${encodeURIComponent(`${title} ${cardUrl}`)}`, "text-emerald-600 border-emerald-300 hover:bg-emerald-50"],
    ["Facebook", Facebook, `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(cardUrl)}`, "text-blue-600 border-blue-300 hover:bg-blue-50"],
  ];
  return (
    <span className="inline-flex items-center gap-1" data-testid={`story-quick-share-${storyId}`}>
      {links.map(([label, Icon, href, cls]) => (
        <a key={label} href={href} target="_blank" rel="noreferrer" title={`Share on ${label}`}
          data-testid={`story-quick-share-${label.toLowerCase()}-${storyId}`}
          onClick={() => track(publicId, label)}
          className={`inline-flex items-center justify-center w-7 h-7 border bg-white ${cls}`}>
          <Icon className="w-3.5 h-3.5" />
        </a>
      ))}
    </span>
  );
}

export function ShareButtons({ url, title, cardUrl, publicId }) {
  const shareUrl = cardUrl || url; // card URL carries OG tags for WhatsApp/FB/X previews
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); } catch { /* fallback below */ }
    track(publicId, "Copy");
    setCopied(true); toast.success("Link copied — pasting it shows the story card"); setTimeout(() => setCopied(false), 1500);
  };
  const native = () => { track(publicId, "Native"); navigator.share?.({ title, url: shareUrl }).catch(() => {}); };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="story-share-buttons">
      {SHARES(shareUrl, title).map(([label, Icon, href]) => (
        <a key={label} href={href} target="_blank" rel="noreferrer" data-testid={`story-share-${label.toLowerCase()}`}
          onClick={() => track(publicId, label)}
          className="inline-flex items-center gap-1 border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-600 hover:border-indigo-400">
          <Icon className="w-3.5 h-3.5" /> {label}
        </a>
      ))}
      <button onClick={copy} data-testid="story-share-copy"
        className="inline-flex items-center gap-1 border border-indigo-300 bg-indigo-50 text-indigo-700 px-2.5 py-1.5 text-xs font-600">
        {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />} Copy Link
      </button>
      {typeof navigator !== "undefined" && navigator.share && (
        <button onClick={native} data-testid="story-share-native"
          className="inline-flex items-center gap-1 border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-600">
          <Share2 className="w-3.5 h-3.5" /> Share…
        </button>
      )}
    </div>
  );
}

/* Renders a story (public page + studio preview). `view` is normalized:
   {story_id, title, subtitle, author_display, date_display, cover_media_id, sections, media} */
export function StoryRender({ view, withAuth = false }) {
  const media = Object.fromEntries((view.media || []).map((m) => [m.id, m]));
  const sections = (view.sections || []).filter((s) => s.body || (s.media_ids || []).length || s.video_url);
  const attached = new Set(sections.flatMap((s) => s.media_ids || []));
  const gallery = (view.media || []).filter((m) => !attached.has(m.id) && m.id !== view.cover_media_id);
  const toc = sections.filter((s) => s.heading).slice(0, 30);
  return (
    <article className="max-w-3xl mx-auto" data-testid="story-render">
      {view.cover_media_id && media[view.cover_media_id] && (
        <img src={mediaUrl(view.story_id, view.cover_media_id, withAuth)} alt={media[view.cover_media_id]?.alt || view.title}
          className="w-full max-h-[420px] object-cover mb-6" data-testid="story-cover" />
      )}
      <h1 className="font-heading font-900 text-4xl sm:text-5xl leading-tight" data-testid="story-title">{view.title}</h1>
      {view.subtitle && <p className="text-lg text-muted-foreground mt-2">{view.subtitle}</p>}
      <div className="text-sm text-muted-foreground mt-3 flex flex-wrap gap-x-3">
        {view.author_display && <span data-testid="story-author">By {view.author_display}</span>}
        {view.date_display && <span data-testid="story-dates">{view.date_display}</span>}
      </div>
      {toc.length > 2 && (
        <nav className="mt-6 border border-zinc-200 bg-zinc-50 p-4 text-sm" data-testid="story-toc">
          <div className="font-700 mb-1.5">In this story</div>
          {toc.map((s) => <a key={s.id} href={`#s-${s.id}`} className="block text-primary hover:underline py-0.5">{s.heading}</a>)}
        </nav>
      )}
      {sections.map((s) => (
        <section key={s.id} id={`s-${s.id}`} className="mt-8 scroll-mt-24" data-testid={`story-section-${s.id}`}>
          {s.heading && <h2 className="font-heading font-900 text-lg md:text-xl mb-2">{s.heading}</h2>}
          {s.body && <div className="text-base leading-relaxed whitespace-pre-line">{s.body}</div>}
          {(s.media_ids || []).map((mid) => media[mid] && (
            <figure key={mid} className="mt-4">
              <img src={mediaUrl(view.story_id, mid, withAuth)} alt={media[mid].alt || ""} className="w-full object-cover" loading="lazy" />
              {media[mid].caption && <figcaption className="text-xs text-muted-foreground mt-1">{media[mid].caption}</figcaption>}
            </figure>
          ))}
          {s.video_url && (videoEmbed(s.video_url)
            ? <div className="mt-4 aspect-video"><iframe src={videoEmbed(s.video_url)} title="Video" className="w-full h-full border-0" allowFullScreen /></div>
            : <a href={s.video_url} target="_blank" rel="noreferrer" className="block mt-3 text-primary hover:underline text-sm">▶ Watch video</a>)}
          {(s.links || []).filter((l) => l.url).length > 0 && (
            <ul className="mt-3 space-y-1">
              {s.links.filter((l) => l.url).map((l, i) => (
                <li key={i}><a href={l.url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-sm inline-flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> {l.title || l.url}</a></li>
              ))}
            </ul>
          )}
        </section>
      ))}
      {gallery.length > 0 && (
        <section className="mt-10" data-testid="story-gallery">
          <h2 className="font-heading font-900 text-lg md:text-xl mb-3">Photo Gallery</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {gallery.map((m) => (
              <figure key={m.id}>
                <img src={mediaUrl(view.story_id, m.id, withAuth)} alt={m.alt || ""} className="w-full h-52 object-cover" loading="lazy" />
                {m.caption && <figcaption className="text-xs text-muted-foreground mt-1">{m.caption}</figcaption>}
              </figure>
            ))}
          </div>
        </section>
      )}
      <footer className="mt-12 pt-5 border-t border-zinc-200 text-sm text-muted-foreground">
        <p className="text-xs">“Story and selected photographs provided by the traveler.”</p>
        <p className="mt-2" data-testid="story-attribution">
          Presented by <a href="https://travelerpulse.pro" target="_blank" rel="noreferrer" className="font-700 text-primary hover:underline">TravelerPulse.pro™</a>
        </p>
      </footer>
    </article>
  );
}

export function dateDisplay(start, end, mode) {
  if (mode === "hidden" || !start) return null;
  const s = new Date(start.slice(0, 10)), e = new Date((end || start).slice(0, 10));
  if (mode === "year") return s.getFullYear() === e.getFullYear() ? `${s.getFullYear()}` : `${s.getFullYear()}–${e.getFullYear()}`;
  if (mode === "month") {
    const f = (d) => d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    return f(s) === f(e) ? f(s) : `${f(s)} – ${f(e)}`;
  }
  return `${start.slice(0, 10)} – ${(end || start).slice(0, 10)}`;
}
